/**
 * authRedirect.js — a dónde mandar a quien no tiene sesión, y cómo volver.
 *
 * El caso que motiva esto: mandamos un correo con un enlace a una página
 * privada (`/Reviews?reviewId=123`, `/settings`, `/Automations`...). Si el
 * usuario no tiene sesión abierta acababa en `/login` a secas y, tras
 * autenticarse, en el destino por defecto — el enlace del correo se perdía por
 * el camino. Aquí se conserva la ruta en `?redirect=` y Login la recupera.
 *
 * Módulo casi PURO: lo consume `api/httpClient.js`, y cualquier import que
 * arrastre módulos de la app montaría un ciclo (httpClient ⇄ este fichero).
 * La única excepción es `lib/rutasPublicas.js`, que es una HOJA —datos y cuatro
 * funciones, sin un solo import— así que no puede cerrar ningún ciclo. Se
 * importa en vez de copiar aquí la lista de idiomas por lo que dice la nota de
 * `PUBLIC_PATHS` doce líneas más abajo: duplicar la lista es exactamente cómo
 * se acaba expulsando al login a un visitante de una página pública.
 *
 * SEGURIDAD. Un destino que viaja en la URL es un vector de open redirect de
 * manual: quien controla el enlace controla a dónde va el usuario **después**
 * de escribir su contraseña, que es justo el momento en que más se fía de lo
 * que ve. `sanitizeRedirect` es la única puerta y se aplica en los DOS
 * extremos: al escribir el parámetro (`buildLoginUrl`) y al leerlo
 * (`readRedirectParam`). Nunca se navega a un valor que no haya pasado por
 * ella.
 */

import { IDIOMAS, IDIOMA_RAIZ, paginaDeLaRuta } from '@/lib/rutasPublicas';

const REDIRECT_PARAM = 'redirect';
// `next` se acepta como alias SOLO de entrada, para poder mandar enlaces
// legibles por correo (`?next=/settings`). Al escribir URLs siempre se emite
// `redirect=`, así que el alias no se propaga ni hay dos nombres que mantener.
const REDIRECT_PARAM_ALIAS = 'next';

const LOGIN_PATH = '/login';
const REGISTER_PATH = '/register';

// Un destino legítimo es una ruta nuestra; nada se acerca a este tamaño.
// El tope corta de raíz los intentos de reventar el parser con megabytes.
const MAX_REDIRECT_LENGTH = 2048;

/**
 * Rutas visibles sin sesión. Vivían inline en `App.jsx`; están aquí porque el
 * interceptor 401 de `httpClient` necesita la MISMA lista — tenerla duplicada
 * es cómo se acaba expulsando al login a un visitante de una página pública.
 */
export const PUBLIC_PATHS = [
  '/login', '/register', '/password-reset', '/terms', '/privacy', '/redirect',
  '/dpa', '/legal-notice', '/cookies', '/join', '/auditor', '/link-pago',
];

/**
 * Públicas con parte variable: `/password-reset-confirm/:uid/:token`, `/en1/:id`.
 *
 * `/v3` está aquí y no en `PUBLIC_PATHS` porque son diez rutas (`/v3`,
 * `/v3/multiubicaciones`, `/v3/hoteles`…) y enumerarlas garantizaría que la
 * próxima se olvide: al visitante le saltaría el login en una página de
 * marketing y nadie lo notaría hasta que lo contara.
 *
 * `/capturas` son las páginas que pintan el producto con datos inventados para
 * poder fotografiarlo. Sus RUTAS solo se registran en desarrollo
 * (`import.meta.env.DEV` en `App.jsx`), así que en producción esto no abre
 * nada: lo único que cambia es que un `/capturas/...` tecleado a mano caiga en
 * el «no encontrado» en vez de en el login, que es la respuesta honesta.
 */
export const PUBLIC_PATH_PREFIXES = [
  '/password-reset-confirm', '/en1', '/v3', '/capturas',
  // La landing del correo pt (`/oferta/<token>`): abre sesión ELLA, así que no
  // puede mandar al login antes de pintarse.
  '/oferta',
];

/**
 * Los prefijos de idioma de las páginas públicas: `/pt`, `/en`, `/de`, `/fr`.
 *
 * El español no está porque vive en la raíz, sin prefijo — sus URLs se
 * reconocen una a una con `paginaDeLaRuta`.
 *
 * Todo lo que cuelga de un prefijo es territorio de marketing, esté o no en la
 * tabla. Así un `/de/lo-que-sea` mal tecleado cae en «no encontrado» y no en el
 * login, que es la respuesta honesta —el mismo criterio que la nota de
 * `/capturas` de aquí arriba—.
 *
 * ⚠️ Que estén en esta lista y no concatenados a mano en ningún sitio importa:
 * la comparación de abajo es `=== prefijo || startsWith(prefijo + '/')`, que es
 * lo único que impide que `/en` se trague `/en1/<id>`, la ruta del LinkTree
 * público. Ese fallo exacto ya tiró producción una vez (`decisiones.md` §52).
 */
const PREFIJOS_IDIOMA = IDIOMAS.filter((idioma) => idioma !== IDIOMA_RAIZ).map((i) => `/${i}`);

// Volver aquí después de autenticarse no tiene sentido y, en el caso de
// `/login`, es un bucle: login → login → login.
const AUTH_PATHS = [LOGIN_PATH, REGISTER_PATH, '/password-reset', '/password-reset-confirm'];

/**
 * Minúsculas y sin barras finales. Las rutas del SPA se registran con casing
 * mixto (`/MyBusiness`) pero react-router matchea sin distinguir mayúsculas,
 * así que cualquier comparación de rutas tiene que normalizar antes.
 * @param {string} pathname
 * @returns {string}
 */
export function normalizePath(pathname) {
  if (typeof pathname !== 'string') return '';
  return pathname.toLowerCase().replace(/\/+$/, '');
}

/**
 * ¿Es una ruta que se puede ver sin sesión?
 * @param {string} pathname
 * @returns {boolean}
 */
export function isPublicPath(pathname) {
  const path = normalizePath(pathname);
  // '/' normaliza a '' — es la landing pública.
  if (path === '') return true;
  if (PUBLIC_PATHS.includes(path)) return true;
  // Las páginas de marketing en sus cinco idiomas. Cubre de una vez las
  // españolas sin prefijo (`/visibilidad-seo`) y las prefijadas que sí están
  // en la tabla; sin esto, `/pt/hoteis` mandaría al login.
  if (paginaDeLaRuta(pathname)) return true;
  // `=== prefix || startsWith(prefix + '/')` y no `startsWith(prefix)` a secas:
  // si no, `/en1xxx` o `/password-reset-confirmar` colarían como públicas — y
  // `/en` se comería `/en1/<id>`, que es el LinkTree.
  return [...PUBLIC_PATH_PREFIXES, ...PREFIJOS_IDIOMA]
    .some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

/**
 * ¿Es una de las pantallas de autenticación?
 * @param {string} pathname
 * @returns {boolean}
 */
export function isAuthPath(pathname) {
  const path = normalizePath(pathname);
  return AUTH_PATHS.some((auth) => path === auth || path.startsWith(`${auth}/`));
}

/**
 * ¿Lleva algún carácter de control (NUL, TAB, CR, LF, DEL...)?
 *
 * Se comprueba por código y no con un regex para que la intención quede a la
 * vista: ninguna ruta legítima los contiene, y son la munición de los ataques
 * que parten una URL en dos para colar un destino distinto del que se lee.
 * @param {string} value
 * @returns {boolean}
 */
function hasControlChars(value) {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

/** Origen actual, o null si no hay ventana (tests fuera de jsdom). */
function currentOrigin() {
  if (typeof window === 'undefined' || !window.location) return null;
  const { origin } = window.location;
  return origin && origin !== 'null' ? origin : null;
}

/**
 * Valida un destino y lo devuelve normalizado, o `null` si no es de fiar
 * (y entonces se usa el destino por defecto de la cuenta).
 *
 * Solo se aceptan rutas internas. Lo que se rechaza, y por qué:
 *   - `https://evil.com`, `javascript:alert(1)`, `data:...` → no empiezan por `/`.
 *   - `//evil.com`, `/\evil.com` → protocol-relative: empiezan por `/` pero el
 *     navegador las resuelve a OTRO dominio. Es el bypass clásico de un
 *     `startsWith('/')` a secas.
 *   - cualquier `\` → el estándar WHATWG lo normaliza a `/`, así que un
 *     backslash a mitad de ruta puede acabar sacándote del sitio.
 *   - caracteres de control (CR, LF, NUL, TAB) → sirven para partir la URL y
 *     colar un destino distinto del que se ve.
 *   - `/login`, `/register`, ... → bucle o pantalla sin sentido tras entrar.
 *
 * Y por último, defensa en profundidad: se resuelve con el parser del propio
 * navegador contra NUESTRO origen y se comprueba que no se ha ido a otro. Lo
 * que se devuelve es la forma reconstruida por el parser, nunca la entrada
 * cruda, así que cualquier rareza de codificación queda normalizada.
 *
 * @param {string|null|undefined} raw
 * @returns {string|null}
 */
export function sanitizeRedirect(raw) {
  if (typeof raw !== 'string') return null;

  const value = raw.trim();
  if (!value || value.length > MAX_REDIRECT_LENGTH) return null;
  if (hasControlChars(value)) return null;
  if (!value.startsWith('/')) return null;
  if (/^\/[/\\]/.test(value)) return null;
  if (value.includes('\\')) return null;
  if (isAuthPath(value.split(/[?#]/)[0])) return null;

  const origin = currentOrigin();
  if (!origin) return value;
  try {
    const url = new URL(value, origin);
    if (url.origin !== origin) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    // URL malformada (un `%` suelto, por ejemplo): no es un destino usable.
    return null;
  }
}

/**
 * Ruta completa —con query y hash— de un `location` (el de `window` o el del
 * router). Es lo que se guarda como destino.
 * @param {{pathname?: string, search?: string, hash?: string}} [location]
 * @returns {string|null}
 */
export function toRedirectValue(location) {
  const loc = location || (typeof window !== 'undefined' ? window.location : null);
  if (!loc) return null;
  return `${loc.pathname || ''}${loc.search || ''}${loc.hash || ''}`;
}

/**
 * URL de una pantalla de auth con el destino colgado, si es válido.
 * @param {string} base '/login' o '/register'
 * @param {string|{pathname?: string, search?: string, hash?: string}|null} [redirect]
 * @returns {string}
 */
export function buildAuthUrl(base, redirect) {
  const target = (redirect && typeof redirect === 'object') ? toRedirectValue(redirect) : redirect;
  const safe = sanitizeRedirect(typeof target === 'string' ? target : null);
  return safe ? `${base}?${REDIRECT_PARAM}=${encodeURIComponent(safe)}` : base;
}

/** @param {string|{pathname?: string, search?: string, hash?: string}|null} [redirect] */
export function buildLoginUrl(redirect) {
  return buildAuthUrl(LOGIN_PATH, redirect);
}

/** @param {string|{pathname?: string, search?: string, hash?: string}|null} [redirect] */
export function buildRegisterUrl(redirect) {
  return buildAuthUrl(REGISTER_PATH, redirect);
}

/**
 * Lee el destino de la URL actual: `redirect` (canónico) o `next` (alias).
 *
 * NO se hace `decodeURIComponent` sobre el resultado: `URLSearchParams.get()`
 * ya devuelve el valor decodificado, y una segunda pasada es justo el bypass
 * que convierte `%252F%252Fevil.com` en `//evil.com` después de haberlo
 * validado. Además rompe destinos legítimos con `%20` o `%26` dentro.
 *
 * @param {URLSearchParams} searchParams
 * @returns {string|null}
 */
export function readRedirectParam(searchParams) {
  if (!searchParams || typeof searchParams.get !== 'function') return null;
  const raw = searchParams.get(REDIRECT_PARAM) ?? searchParams.get(REDIRECT_PARAM_ALIAS);
  return sanitizeRedirect(raw);
}
